import type { TermsContent } from "./types";

export const termsEn: TermsContent = {
  locale: "en",
  seo: {
    title: "Terms of Use - Prismark",
    description:
      "Terms of Use for Prismark, a lightweight pro camera app for iOS."
  },
  title: "Terms of Use",
  updated: "Last updated: September 13, 2026",
  intro: [
    "Welcome to Prismark. Please read these Terms of Use carefully before using Prismark. By downloading, installing, accessing, or using Prismark, you acknowledge that you have read, understood, and agree to be bound by these Terms.",
    "If you do not agree to these Terms, please stop using Prismark."
  ],
  sections: [
    {
      title: "1. About Prismark",
      body: [
        "Prismark is a lightweight pro camera app for iOS, designed for photo capture, video recording, dual-camera recording, gallery review, and related shooting assistance features.",
        "Prismark is designed to provide a lightweight yet professional camera experience. Some features may depend on your device model, iOS version, system permissions, hardware capabilities, or regional settings."
      ]
    },
    {
      title: "2. Use of the App",
      body: ["You agree to use Prismark in a lawful, reasonable, and respectful manner.", "You must not use Prismark to:"],
      bullets: [
        "violate another person's privacy, portrait rights, reputation, intellectual property rights, or other legal rights;",
        "capture, record, or share images, videos, audio, or related content without necessary authorization or consent;",
        "engage in hidden recording, harassment, stalking, intimidation, fraud, or other unlawful, infringing, or inappropriate activities;",
        "interfere with, damage, bypass, or attempt to circumvent Prismark's normal functionality, security features, or technical restrictions;",
        "violate applicable laws, regulations, platform rules, or App Store policies."
      ]
    },
    {
      title: "3. User Content",
      body: [
        "You retain ownership of the photos, videos, and other content that you capture, record, save, or manage using Prismark.",
        "Prismark does not claim ownership of the photos, videos, or other user content you create with the app.",
        "You understand and agree that you are responsible for how you capture, store, share, publish, or otherwise use your content. You must ensure that your content and your use of it do not infringe any third-party rights or violate applicable laws and regulations."
      ]
    },
    {
      title: "4. Permissions and Device Access",
      body: [
        "To provide its core features, Prismark may request access to your camera, microphone, photo library, or other necessary system permissions.",
        "These permissions are mainly used to:"
      ],
      bullets: [
        "take photos and record videos with the camera;",
        "record audio when capturing video;",
        "save photos or videos to your system photo library;",
        "view, select, or manage related media from your photo library."
      ]
    },
    {
      title: "5. Feature Availability",
      body: [
        "Prismark aims to provide a stable and smooth experience, but we do not guarantee that all features will be available on all devices, system versions, or environments at all times.",
        "Some features, especially dual-camera recording, HDR video, certain shooting assistance features, or hardware-related capabilities, may depend on device model, iOS version, system permissions, hardware capabilities, or system limitations.",
        "We may update, modify, suspend, or discontinue any part of Prismark's features, interface, content, or services based on product planning, technical limitations, system changes, or operational needs."
      ]
    },
    {
      title: "6. Paid Features",
      body: [
        "Prismark does not require an account or login. Its core camera features are included at no additional cost: photo and video capture, manual exposure controls, the built-in filters, photo watermarks, and the shooting assistance tools.",
        "Three features are part of Prismark Pro: importing your own LUTs, ProRAW and RAW output, and dual-camera picture-in-picture recording. Prismark Pro is a one-time in-app purchase, not a subscription. There are no recurring charges, no renewals, and nothing to cancel. Current pricing and availability, both for the app itself and for Prismark Pro, are shown on the App Store product page and inside the app before you buy.",
        "Prismark Pro is tied to the Apple Account used to buy it. You can restore it on your other devices, or after reinstalling, with \"Restore Purchases\" in the app. If you downloaded Prismark before it offered a paid tier, these features remain available to you at no charge.",
        "Please review the relevant information carefully before making a purchase. Purchases and refunds are generally handled by the App Store and Apple's applicable rules."
      ]
    },
    {
      title: "7. Third-Party Services",
      body: [
        "Prismark may use iOS system capabilities, App Store services, the system photo library, system sharing features, or other third-party services when necessary.",
        "Third-party services may be subject to their own terms and privacy policies. We do not control the content, rules, or availability of third-party services and are not responsible for issues caused by them."
      ]
    },
    {
      title: "8. Intellectual Property",
      body: [
        "The Prismark name, icon, interface design, visual elements, software code, copy, page content, and related brand assets are protected by applicable intellectual property laws.",
        "Without our prior written permission, you may not copy, modify, distribute, sell, rent, reverse engineer, decompile, attempt to extract source code from, or otherwise use any part of Prismark in an unauthorized way.",
        "These Terms do not transfer any ownership of Prismark or related content to you. You are granted only a limited, non-exclusive, non-transferable license to use Prismark in accordance with these Terms."
      ]
    },
    {
      title: "9. Disclaimer",
      body: [
        "Prismark is provided on an \"as is\" and \"as available\" basis. We work to provide a stable and reliable product experience, but we do not guarantee that Prismark will be error-free, uninterrupted, always secure, or meet all of your expectations.",
        "You understand and agree that when using Prismark to capture, record, save, or share content, you are responsible for judging the shooting environment, device condition, legality of the content, and related risks.",
        "To the extent permitted by applicable law, we are not responsible for content loss, failed captures, unavailable features, or other losses caused by device issues, system limitations, permission settings, user actions, third-party services, network conditions, or other factors beyond our control."
      ]
    },
    {
      title: "10. Limitation of Liability",
      body: [
        "To the maximum extent permitted by applicable law, Prismark and its developers or operators shall not be liable for any indirect, incidental, special, punitive, or consequential damages, including but not limited to data loss, content damage, loss of profits, business interruption, or losses arising from the use of or inability to use Prismark.",
        "If applicable law does not allow certain exclusions or limitations of liability, this section will apply to the maximum extent permitted by law."
      ]
    },
    {
      title: "11. Changes to These Terms",
      body: [
        "We may update these Terms from time to time. Updated Terms will be posted on Prismark's website or relevant app pages with the updated date.",
        "If you continue to use Prismark after the Terms are updated, you accept the updated Terms. If you do not agree to the updated Terms, please stop using Prismark."
      ]
    },
    {
      title: "12. Termination",
      body: [
        "If you violate these Terms or use Prismark in an unlawful, inappropriate, or infringing manner, we may restrict, suspend, or terminate your access to Prismark to the extent permitted by applicable law.",
        "You may also stop using Prismark at any time and delete the app from your device."
      ]
    },
    {
      title: "13. Contact Us",
      body: ["If you have any questions, feedback, or requests about these Terms, please contact us at:", "Email: axqora@gmail.com"]
    }
  ],
  privacyLink: {
    body: "For information about how Prismark handles permissions and data, please read our Privacy Policy.",
    label: "Privacy Policy",
    href: "/privacy"
  }
};
